// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/MentorOperacional/MentorOperacional.tsx
//  Painel dos Agentes Operacionais: escolher agente, pedir uma ação, ver o que
//  o agente consultou/respondeu e CONFIRMAR as ações de escrita (o gate).
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  Bot, Send, Loader2, ShieldCheck, CheckCircle2, AlertTriangle, Sparkles, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { agentesService } from '@/features/agentes/services/agentesService';
import type { AgenteResumo, AgenteTurnoResultado, AcaoProposta } from '@/features/agentes/types';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import PropostasPendentes from './PropostasPendentes';

const SUGESTOES = [
  'Quem está devendo? Liste os inadimplentes.',
  'Quais contas a pagar vencem nesta semana?',
  'Prepare a cobrança dos clientes em atraso há mais de 15 dias.',
];

function prettyJson(raw: string): string {
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

export default function MentorOperacional() {
  const [agentes, setAgentes] = useState<AgenteResumo[]>([]);
  const [agente, setAgente] = useState<string>('');
  const [pedido, setPedido] = useState('');
  const [carregando, setCarregando] = useState(false);

  const [resultado, setResultado] = useState<AgenteTurnoResultado | null>(null);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [verArgs, setVerArgs] = useState<Set<string>>(new Set());
  const [confirmando, setConfirmando] = useState(false);

  const agenteAtual = useMemo(
    () => agentes.find((a) => a.codigo === agente),
    [agentes, agente],
  );

  useEffect(() => {
    agentesService.listar()
      .then((lista) => {
        setAgentes(lista);
        if (lista.length > 0) setAgente(lista[0].codigo);
      })
      .catch((e) => toast.error(e?.message ?? 'Falha ao carregar agentes.'));
  }, []);

  async function executar() {
    if (!agente || !pedido.trim()) return;
    setCarregando(true);
    setResultado(null);
    setSelecionadas(new Set());
    setVerArgs(new Set());
    try {
      const r = await agentesService.processar(agente, pedido.trim());
      setResultado(r);
      // pré-seleciona todas as propostas para facilitar
      setSelecionadas(new Set(r.propostasPendentes.map((p) => p.propostaId)));
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Falha ao processar o pedido.');
    } finally {
      setCarregando(false);
    }
  }

  function toggle(set: Set<string>, setter: (s: Set<string>) => void, id: string) {
    const novo = new Set(set);
    if (novo.has(id)) novo.delete(id); else novo.add(id);
    setter(novo);
  }

  async function confirmar() {
    if (!resultado || selecionadas.size === 0) return;
    const aprovadas: AcaoProposta[] = resultado.propostasPendentes.filter(
      (p) => selecionadas.has(p.propostaId),
    );
    setConfirmando(true);
    try {
      const r = await agentesService.confirmar(agente, aprovadas);
      const ok = r.acoesExecutadas.filter((a) => a.sucesso).length;
      toast.success(`${ok} de ${r.acoesExecutadas.length} ação(ões) executada(s).`);
      // limpa as propostas confirmadas e mostra o que foi executado
      setResultado({
        ...resultado,
        propostasPendentes: [],
        acoesExecutadas: [...resultado.acoesExecutadas, ...r.acoesExecutadas],
      });
      setSelecionadas(new Set());
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Falha ao confirmar as ações.');
    } finally {
      setConfirmando(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4">
      {/* Cabeçalho */}
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-primary/10 p-2 text-primary"><Bot className="h-6 w-6" /></div>
        <div>
          <h1 className="text-xl font-bold">Mentor IA — Operacional</h1>
          <p className="text-sm text-muted-foreground">
            Agentes que executam o trabalho. Ações que gravam pedem sua confirmação.
          </p>
        </div>
      </div>

      {/* Fila de propostas persistidas (canais sem UI, ex.: MCP) */}
      <PropostasPendentes />

      {/* Seleção de agente + pedido */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">O que você precisa?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select value={agente} onValueChange={setAgente}>
              <SelectTrigger className="sm:w-64"><SelectValue placeholder="Escolha o agente" /></SelectTrigger>
              <SelectContent>
                {agentes.map((a) => (
                  <SelectItem key={a.codigo} value={a.codigo}>{a.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {agenteAtual && (
              <span className="text-xs text-muted-foreground">{agenteAtual.descricao}</span>
            )}
          </div>

          <Textarea
            value={pedido}
            onChange={(e) => setPedido(e.target.value)}
            placeholder="Ex.: liste os inadimplentes e prepare a cobrança dos que estão há mais de 15 dias em atraso"
            rows={3}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) executar(); }}
          />

          <div className="flex flex-wrap gap-2">
            {SUGESTOES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setPedido(s)}
                className="rounded-full border px-3 py-1 text-xs text-muted-foreground hover:bg-muted"
              >
                {s}
              </button>
            ))}
          </div>

          <div className="flex justify-end">
            <Button onClick={executar} disabled={carregando || !agente || !pedido.trim()}>
              {carregando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Executar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resposta do agente */}
      {resultado && (
        <>
          {resultado.resposta && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-4 w-4 text-primary" /> Resposta do agente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{resultado.resposta}</p>
              </CardContent>
            </Card>
          )}

          {/* Ações executadas (leituras + confirmadas) */}
          {resultado.acoesExecutadas.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Ações executadas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {resultado.acoesExecutadas.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    {a.sucesso
                      ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                      : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
                    <span>
                      <span className="font-mono text-xs text-muted-foreground">{a.ferramenta}</span>
                      {' — '}{a.erro ?? a.resumo}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Gate — propostas de escrita pendentes de confirmação */}
          {resultado.propostasPendentes.length > 0 && (
            <Card className="border-amber-300/60">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShieldCheck className="h-4 w-4 text-amber-600" />
                  Ações que precisam da sua confirmação
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {resultado.propostasPendentes.map((p) => {
                  const aberta = verArgs.has(p.propostaId);
                  return (
                    <div key={p.propostaId} className="rounded-md border p-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selecionadas.has(p.propostaId)}
                          onCheckedChange={() => toggle(selecionadas, setSelecionadas, p.propostaId)}
                          className="mt-1"
                        />
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{p.resumoHumano}</span>
                            {p.permissaoNecessaria && (
                              <Badge variant="outline" className="text-[10px]">{p.permissaoNecessaria}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="font-mono">{p.ferramenta}</span>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 hover:text-foreground"
                              onClick={() => toggle(verArgs, setVerArgs, p.propostaId)}
                            >
                              <Eye className="h-3 w-3" /> {aberta ? 'ocultar' : 'ver'} detalhes
                            </button>
                          </div>
                          {aberta && (
                            <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted/80 p-2 text-[11px]">
                              {prettyJson(p.argumentosJson)}
                            </pre>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {selecionadas.size} de {resultado.propostasPendentes.length} selecionada(s)
                  </span>
                  <Button
                    onClick={confirmar}
                    disabled={confirmando || selecionadas.size === 0}
                    className={cn(selecionadas.size > 0 && 'bg-amber-600 hover:bg-amber-700')}
                  >
                    {confirmando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                    Confirmar e executar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
